import React, { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Copy, Download, Play, Loader2, ChevronDown, ChevronRight, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

// Iter 66 — Founder Intelligence Dashboard
// Admin-only. Read-only. All work is on-demand — no auto-runs.

const SAMPLE_SIZES = ["100", "250", "500", "1000", "all"];

function downloadBlob(name, content, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function useCopy() {
  return useCallback((text) => {
    if (!text) {
      toast.error("Nothing to copy");
      return;
    }
    navigator.clipboard.writeText(text).then(
      () => toast.success("Copied"),
      () => toast.error("Copy failed"),
    );
  }, []);
}

// ---------- reusable section shell ----------
function Section({ id, title, description, controls, result, running, error, onRun,
                   onCopy, onDownloadMd, onDownloadJson, children }) {
  const [open, setOpen] = useState(true);
  return (
    <section
      className="border border-neutral-800 rounded-2xl bg-[#0f0f10] p-5 sm:p-6 mb-6"
      data-testid={`fi-section-${id}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 text-left"
          data-testid={`fi-toggle-${id}`}
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <h2 className="font-display font-light text-xl sm:text-2xl tracking-tight">{title}</h2>
        </button>
        <div className="flex items-center gap-2">
          {controls}
          <button
            type="button"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-950 border border-emerald-800 hover:bg-emerald-900 text-emerald-100 text-xs disabled:opacity-50"
            onClick={onRun}
            disabled={running}
            data-testid={`fi-run-${id}`}
          >
            {running ? <Loader2 className="animate-spin" size={14} /> : <Play size={14} />}
            {running ? "Running…" : "Run"}
          </button>
        </div>
      </div>
      {description && <p className="text-xs text-neutral-500 mt-2 max-w-3xl">{description}</p>}
      {open && (
        <div className="mt-4">
          {children}
          {error && (
            <div
              className="mt-3 text-xs text-red-400 border border-red-900/40 bg-red-950/20 rounded-lg p-3"
              data-testid={`fi-error-${id}`}
            >
              <strong>Error:</strong> {error}
            </div>
          )}
          {result && (
            <div className="mt-4">
              <div className="flex items-center gap-2 text-xs text-neutral-500 mb-2 flex-wrap">
                <span data-testid={`fi-timestamp-${id}`}>Generated {result.generated_at}</span>
                <span>·</span>
                <span data-testid={`fi-duration-${id}`}>{result.duration_ms} ms</span>
                <span>·</span>
                <span data-testid={`fi-rows-${id}`}>{result.rows_processed} rows</span>
                <span className="grow" />
                <button
                  type="button"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded border border-neutral-800 hover:bg-neutral-900"
                  onClick={onCopy}
                  data-testid={`fi-copy-${id}`}
                >
                  <Copy size={12} /> Copy
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded border border-neutral-800 hover:bg-neutral-900"
                  onClick={onDownloadMd}
                  data-testid={`fi-dl-md-${id}`}
                >
                  <Download size={12} /> .md
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded border border-neutral-800 hover:bg-neutral-900"
                  onClick={onDownloadJson}
                  data-testid={`fi-dl-json-${id}`}
                >
                  <Download size={12} /> .json
                </button>
              </div>
              <pre
                className="text-xs leading-relaxed bg-[#050505] border border-neutral-900 rounded-xl p-4 overflow-x-auto whitespace-pre-wrap"
                data-testid={`fi-markdown-${id}`}
              >
                {result.markdown || "_(empty)_"}
              </pre>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ---------- controls ----------
function SampleSelect({ value, onChange, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-black border border-neutral-800 rounded px-2 py-1 text-xs"
      data-testid={testid}
    >
      {SAMPLE_SIZES.map((s) => (
        <option key={s} value={s}>
          {s === "all" ? "All time" : `Last ${s}`}
        </option>
      ))}
    </select>
  );
}

function NumberInput({ value, onChange, testid, label }) {
  return (
    <label className="flex items-center gap-1 text-xs text-neutral-500">
      {label}
      <input
        type="number"
        min={1}
        max={3650}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value || "0", 10))}
        className="bg-black border border-neutral-800 rounded px-2 py-1 text-xs w-20 text-neutral-100"
        data-testid={testid}
      />
    </label>
  );
}

// ---------- runners ----------
function useRunner(endpoint) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const run = useCallback(async (body = {}) => {
    setRunning(true);
    setError(null);
    try {
      const { data } = await api.post(endpoint, body);
      setResult(data);
      if (data.error) setError(data.error);
    } catch (e) {
      // Iter 66.2 — if the founder-intel router is disabled by the kill
      // switch, the API returns 404. Surface a clear, friendly message
      // rather than a scary axios error.
      const status = e?.response?.status;
      if (status === 404) {
        setError(
          "Founder Intelligence is currently DISABLED on this deployment. " +
          "Set FOUNDER_INTEL_ENABLED=1 in the backend env and restart to enable.",
        );
      } else {
        setError(e?.response?.data?.detail || e?.message || "unknown error");
      }
    } finally {
      setRunning(false);
    }
  }, [endpoint]);
  return { running, result, error, run, setResult };
}

// ---------- page ----------
export default function FounderIntel() {
  const copy = useCopy();

  // Section 0 — master report
  const full = useRunner("/admin/founder-intel/full-report");

  // Section 1
  const conv = useRunner("/admin/founder-intel/conversation-report");
  const [convSample, setConvSample] = useState("500");
  const [convLookback, setConvLookback] = useState(180);

  // Section 2
  const voice = useRunner("/admin/founder-intel/customer-voice");
  const [voiceSample, setVoiceSample] = useState("500");
  const [voiceLookback, setVoiceLookback] = useState(365);
  const [voiceSilence, setVoiceSilence] = useState(14);

  // Section 3
  const paying = useRunner("/admin/founder-intel/paying-customers");

  // Section 4
  const failures = useRunner("/admin/founder-intel/video-failures");
  const [failLookback, setFailLookback] = useState(30);

  // Section 5
  const retention = useRunner("/admin/founder-intel/retention");
  const [retLookback, setRetLookback] = useState(90);

  // Section 6
  const funnel = useRunner("/admin/founder-intel/funnel");
  const [funnelLookback, setFunnelLookback] = useState(30);

  // Section 7
  const ask = useRunner("/admin/founder-intel/ask");
  const [question, setQuestion] = useState("");
  const [askMode, setAskMode] = useState("lookup");
  const [askReportKind, setAskReportKind] = useState("voice");

  const chooseReport = () => {
    if (askReportKind === "voice" && voice.result?.json) return voice.result.json;
    if (askReportKind === "conversation" && conv.result?.json) return conv.result.json;
    if (askReportKind === "funnel" && funnel.result?.json) return funnel.result.json;
    if (askReportKind === "retention" && retention.result?.json) return retention.result.json;
    if (askReportKind === "video-failures" && failures.result?.json) return failures.result.json;
    return null;
  };

  const askNow = () => {
    const report = chooseReport();
    if (!report) {
      toast.error(`Load the ${askReportKind} report first`);
      return;
    }
    if (!question.trim()) {
      toast.error("Type a question first");
      return;
    }
    ask.run({ question, mode: askMode, report_kind: askReportKind, report });
  };

  const dl = (name, obj) => (obj?.markdown ? downloadBlob(name + ".md", obj.markdown, "text/markdown") : null);
  const dlJson = (name, obj) => (obj?.json ? downloadBlob(name + ".json", JSON.stringify(obj.json, null, 2), "application/json") : null);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-neutral-100 font-sans">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-10">
          <div>
            <p className="label-eyebrow mb-2 text-neutral-500 text-xs uppercase tracking-widest">
              admin · founder intelligence
            </p>
            <h1 className="font-display font-light text-3xl sm:text-4xl tracking-tight" data-testid="fi-title">
              Founder Intelligence
            </h1>
            <p className="text-sm text-neutral-500 mt-2 max-w-2xl leading-relaxed">
              Every report is generated on demand from production data. Nothing runs automatically.
              Nothing is written to Mongo. Founder-only.
            </p>
          </div>
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-800 hover:bg-neutral-900 text-xs text-neutral-300"
            data-testid="fi-back-to-admin"
          >
            <ArrowLeft size={14} /> Back to Web Control Room
          </Link>
        </div>

        {/* Section 0 — Master report */}
        <section
          className="border border-emerald-900/40 bg-emerald-950/10 rounded-2xl p-5 sm:p-6 mb-8"
          data-testid="fi-section-full"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="label-eyebrow mb-2 text-emerald-500/70 text-xs uppercase tracking-widest">
                weekly founder report
              </p>
              <h2 className="font-display font-light text-2xl sm:text-3xl tracking-tight text-emerald-100">
                Run Full Founder Intelligence Report
              </h2>
              <p className="text-xs text-neutral-500 mt-2 max-w-3xl leading-relaxed">
                Runs every section in sequence (1–6). If one fails, the others still complete and
                the failed section is marked. Returns one combined Markdown, one combined JSON, one
                timestamp, one total duration and one total row count, with a per-section status
                table and a summary header at the top.
              </p>
            </div>
            <button
              type="button"
              onClick={() => full.run({})}
              disabled={full.running}
              className="inline-flex items-center gap-2 px-4 py-3 rounded-lg bg-emerald-800 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 border border-emerald-600"
              data-testid="fi-run-full"
            >
              {full.running ? <Loader2 className="animate-spin" size={16} /> : <Play size={16} />}
              {full.running ? "Running full report…" : "Run Full Founder Intelligence Report"}
            </button>
          </div>

          {full.error && (
            <div
              className="mt-3 text-xs text-red-400 border border-red-900/40 bg-red-950/20 rounded-lg p-3"
              data-testid="fi-error-full"
            >
              <strong>Error:</strong> {full.error}
            </div>
          )}

          {full.result && (
            <div className="mt-5">
              <div className="flex items-center gap-2 text-xs text-neutral-400 mb-3 flex-wrap">
                <span data-testid="fi-timestamp-full">Generated {full.result.generated_at}</span>
                <span>·</span>
                <span data-testid="fi-duration-full">{full.result.duration_ms} ms total</span>
                <span>·</span>
                <span data-testid="fi-rows-full">{full.result.rows_processed} rows total</span>
                <span className="grow" />
                <button
                  type="button"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded border border-neutral-800 hover:bg-neutral-900"
                  onClick={() => copy(full.result.markdown)}
                  data-testid="fi-copy-full"
                >
                  <Copy size={12} /> Copy full report
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded border border-neutral-800 hover:bg-neutral-900"
                  onClick={() => dl("full-founder-intel", full.result)}
                  data-testid="fi-dl-md-full"
                >
                  <Download size={12} /> Combined .md
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded border border-neutral-800 hover:bg-neutral-900"
                  onClick={() => dlJson("full-founder-intel", full.result)}
                  data-testid="fi-dl-json-full"
                >
                  <Download size={12} /> Combined .json
                </button>
              </div>

              {full.result.json?.sections?.length ? (
                <div className="mb-4 text-xs" data-testid="fi-full-section-status">
                  <div className="mb-1 text-neutral-500">Per-section status</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {full.result.json.sections.map((s) => (
                      <div
                        key={s.label}
                        className={
                          "border rounded-lg px-3 py-2 flex items-center justify-between gap-3 " +
                          (s.status === "ok"
                            ? "border-emerald-900/40 bg-emerald-950/10"
                            : "border-red-900/40 bg-red-950/20")
                        }
                        data-testid={`fi-full-section-${s.label.split(" ")[0]}`}
                      >
                        <div>
                          <div className="text-neutral-200">{s.label}</div>
                          {s.error && (
                            <div className="text-red-300 text-[11px] mt-1">{s.error}</div>
                          )}
                        </div>
                        <div className="text-right text-neutral-500 text-[11px] whitespace-nowrap">
                          <div>{s.status === "ok" ? "✅ ok" : "❌ failed"}</div>
                          <div>{s.rows} rows · {s.duration_ms} ms</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <pre
                className="text-xs leading-relaxed bg-[#050505] border border-neutral-900 rounded-xl p-4 overflow-x-auto whitespace-pre-wrap max-h-[900px]"
                data-testid="fi-markdown-full"
              >
                {full.result.markdown || "_(empty)_"}
              </pre>
            </div>
          )}
        </section>

        <Section
          id="conv"
          title="1 · Conversation Intelligence"
          description="Run the last-N-conversations quantitative analyser."
          controls={
            <>
              <SampleSelect value={convSample} onChange={setConvSample} testid="fi-conv-sample" />
              <NumberInput value={convLookback} onChange={setConvLookback} label="lookback d" testid="fi-conv-lookback" />
            </>
          }
          result={conv.result}
          running={conv.running}
          error={conv.error}
          onRun={() => conv.run({ sample: convSample, lookback_days: convLookback })}
          onCopy={() => copy(conv.result?.markdown)}
          onDownloadMd={() => dl("conversation-report", conv.result)}
          onDownloadJson={() => dlJson("conversation-report", conv.result)}
        />

        <Section
          id="voice"
          title="2 · Customer Voice"
          description="Qualitative analyser — trust, dropout, emotional turnaround, top-25 quotes, blind spots."
          controls={
            <>
              <SampleSelect value={voiceSample} onChange={setVoiceSample} testid="fi-voice-sample" />
              <NumberInput value={voiceLookback} onChange={setVoiceLookback} label="lookback d" testid="fi-voice-lookback" />
              <NumberInput value={voiceSilence} onChange={setVoiceSilence} label="silence d" testid="fi-voice-silence" />
            </>
          }
          result={voice.result}
          running={voice.running}
          error={voice.error}
          onRun={() => voice.run({ sample: voiceSample, lookback_days: voiceLookback, silence_days: voiceSilence })}
          onCopy={() => copy(voice.result?.markdown)}
          onDownloadMd={() => dl("customer-voice", voice.result)}
          onDownloadJson={() => dlJson("customer-voice", voice.result)}
        />

        <Section
          id="paying"
          title="3 · First Paying Customers"
          description="One RCA card per paying user found in production."
          result={paying.result}
          running={paying.running}
          error={paying.error}
          onRun={() => paying.run({})}
          onCopy={() => copy(paying.result?.markdown)}
          onDownloadMd={() => dl("paying-customers", paying.result)}
          onDownloadJson={() => dlJson("paying-customers", paying.result)}
        >
          {paying.result?.json?.cards?.length ? (
            <div className="text-xs text-neutral-500" data-testid="fi-paying-count">
              {paying.result.json.count} paying customer(s) analysed. Full markdown below; each card is separated by
              a horizontal rule.
            </div>
          ) : null}
        </Section>

        <Section
          id="failures"
          title="4 · Video Failure Intelligence"
          description="Rejection reasons + Gemini/upload/normalization/transport + per-dimension success rates."
          controls={<NumberInput value={failLookback} onChange={setFailLookback} label="lookback d" testid="fi-fail-lookback" />}
          result={failures.result}
          running={failures.running}
          error={failures.error}
          onRun={() => failures.run({ lookback_days: failLookback })}
          onCopy={() => copy(failures.result?.markdown)}
          onDownloadMd={() => dl("video-failures", failures.result)}
          onDownloadJson={() => dlJson("video-failures", failures.result)}
        />

        <Section
          id="retention"
          title="5 · Retention"
          description="D0/D1/D7/D30 cohort retention + averages."
          controls={<NumberInput value={retLookback} onChange={setRetLookback} label="lookback d" testid="fi-ret-lookback" />}
          result={retention.result}
          running={retention.running}
          error={retention.error}
          onRun={() => retention.run({ lookback_days: retLookback })}
          onCopy={() => copy(retention.result?.markdown)}
          onDownloadMd={() => dl("retention", retention.result)}
          onDownloadJson={() => dlJson("retention", retention.result)}
        />

        <Section
          id="funnel"
          title="6 · Conversion Funnel"
          description="Sessions → first message → upload → success → 2nd review → paywall → checkout → paid → active Plus."
          controls={<NumberInput value={funnelLookback} onChange={setFunnelLookback} label="lookback d" testid="fi-funnel-lookback" />}
          result={funnel.result}
          running={funnel.running}
          error={funnel.error}
          onRun={() => funnel.run({ lookback_days: funnelLookback })}
          onCopy={() => copy(funnel.result?.markdown)}
          onDownloadMd={() => dl("funnel", funnel.result)}
          onDownloadJson={() => dlJson("funnel", funnel.result)}
        />

        {/* Section 7 has its own layout (question box + mode toggle) */}
        <section
          className="border border-neutral-800 rounded-2xl bg-[#0f0f10] p-5 sm:p-6 mb-6"
          data-testid="fi-section-ask"
        >
          <h2 className="font-display font-light text-xl sm:text-2xl tracking-tight">7 · Founder Questions</h2>
          <p className="text-xs text-neutral-500 mt-2 max-w-3xl">
            Ask a question. Answers come strictly from the loaded report. In LLM mode, GPT-5.2 must
            quote the report — if evidence is missing it replies &quot;insufficient evidence&quot;. Load a report from
            Sections 1–6 first, then choose which one to answer against.
          </p>
          <div className="mt-4 flex flex-wrap gap-2 items-end">
            <label className="flex flex-col gap-1 text-xs text-neutral-500 grow min-w-[260px]">
              Question
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Why are people leaving?"
                className="bg-black border border-neutral-800 rounded px-2 py-2 text-sm text-neutral-100"
                data-testid="fi-ask-question"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-neutral-500">
              Report to search
              <select
                value={askReportKind}
                onChange={(e) => setAskReportKind(e.target.value)}
                className="bg-black border border-neutral-800 rounded px-2 py-2 text-xs"
                data-testid="fi-ask-report-kind"
              >
                <option value="voice">Customer voice</option>
                <option value="conversation">Conversation</option>
                <option value="funnel">Funnel</option>
                <option value="retention">Retention</option>
                <option value="video-failures">Video failures</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-neutral-500">
              Mode
              <select
                value={askMode}
                onChange={(e) => setAskMode(e.target.value)}
                className="bg-black border border-neutral-800 rounded px-2 py-2 text-xs"
                data-testid="fi-ask-mode"
              >
                <option value="lookup">Evidence lookup (default)</option>
                <option value="llm">LLM (strictly grounded)</option>
              </select>
            </label>
            <button
              type="button"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-950 border border-emerald-800 hover:bg-emerald-900 text-emerald-100 text-xs disabled:opacity-50"
              onClick={askNow}
              disabled={ask.running}
              data-testid="fi-ask-run"
            >
              {ask.running ? <Loader2 className="animate-spin" size={14} /> : <Play size={14} />}
              {ask.running ? "Asking…" : "Ask"}
            </button>
          </div>
          {ask.error && (
            <div className="mt-3 text-xs text-red-400 border border-red-900/40 bg-red-950/20 rounded-lg p-3" data-testid="fi-ask-error">
              <strong>Error:</strong> {ask.error}
            </div>
          )}
          {ask.result && (
            <div className="mt-4">
              <div className="flex items-center gap-2 text-xs text-neutral-500 mb-2 flex-wrap">
                <span data-testid="fi-ask-duration">{ask.result.duration_ms} ms</span>
                <span className="grow" />
                <button
                  type="button"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded border border-neutral-800 hover:bg-neutral-900"
                  onClick={() => copy(ask.result.markdown)}
                  data-testid="fi-ask-copy"
                >
                  <Copy size={12} /> Copy
                </button>
              </div>
              <pre
                className="text-xs leading-relaxed bg-[#050505] border border-neutral-900 rounded-xl p-4 overflow-x-auto whitespace-pre-wrap"
                data-testid="fi-ask-answer"
              >
                {ask.result.markdown || "_(empty)_"}
              </pre>
            </div>
          )}
        </section>

        <p className="text-xs text-neutral-600 mt-6">
          Iter 66 — Founder Intelligence Dashboard · read-only · admin-only. No auto-runs. No DB writes.
        </p>
      </div>
    </div>
  );
}
